import jwt_decode, { JwtPayload } from 'jwt-decode';
import api from './api';
import { DadosPermissoesDoToken, permissoesEfetivas } from './permissoes-token';

interface Jwt extends JwtPayload {
    id: string;
    name: string;
    email: string;
    role: string[];
    empresa: string;
    unique_name: string;
    empresa_nome: string;
    administrador: string;
    empresa_id: string;
    periodoDeTeste: string;
    planoGratuito: string;
    expiraEm: string;
    limiteDeVeiculos: number;
    dataExpiracao: string;
    tipoPlano: string;
    idExpo: string;
    dataCadastro: string;
    integradoLocaSmartCheck: string;
    empresaTemFiliais: string;
    possuiSite: string;
    onboardingPendente: string;
}

export function obterIdTenent(token: string): string {
    if (!token) {
        return '';
    }

    let id = jwt_decode<Jwt>(token).empresa;
    return id
        ? id
        : '';
}

export function obterIdUsuario(token: string): string {
    if (!token) {
        return '';
    }

    let id = jwt_decode<Jwt>(token).id;
    return id
        ? id
        : '';
}

export function obterIdExpo(token: string): string {
    if (!token) {
        return '';
    }

    let id = jwt_decode<Jwt>(token).idExpo;
    if(!id?.startsWith("ExponentPushToken")) {
        return '';
    }
    return id
        ? id
        : '';
}

export function obterNomeUsuario(token: string): string {
    if (!token) {
        return 'NÃO IDENTIFICADO';
    }

    let usuario = jwt_decode<Jwt>(token).name;
    if (!usuario) {
        usuario = jwt_decode<Jwt>(token).unique_name;
    }

    if (!usuario) {
        return 'NÃO IDENTIFICADO';
    }

    const nomes = usuario.split(' ');
    const primeiroNome = nomes[0];
    const sobrenomesIniciais = nomes.slice(1).map(sobrenome => sobrenome.charAt(0)).join(' ');

    const nomeFormatado = `${primeiroNome} ${sobrenomesIniciais}`.trim().toUpperCase();

    return usuario.length > 22 ? nomeFormatado : usuario.toUpperCase();
}


export function obterNomeEmpresa(token: string): string {
    if (!token) {
        return 'Não identificado';
    }

    let empresa = jwt_decode<Jwt>(token).empresa_nome;

    return empresa ? empresa : 'Empresa não identificada';
}

export function estaEmPeriodoDeTeste(token: string): boolean {
    if (!token) {
        return false;
    }

    let estaEmPeriodoDeTeste = Boolean(jwt_decode<Jwt>(token).periodoDeTeste);

    return estaEmPeriodoDeTeste;
}

export function ehPlanoGratuito(token: string): boolean {
    if (!token) {
        return false;
    }

    let ehPlanoGratuito = Boolean(jwt_decode<Jwt>(token).planoGratuito);

    return ehPlanoGratuito;
}

export function expiraEm(token: string): string {
    if (!token) {
        return '';
    }

    let formattedDate = jwt_decode<Jwt>(token).dataExpiracao;

    return formattedDate;
}

export function dataExpiracao(token: string): string {
    if (!token) {
        return '';
    }

    let formattedDate = jwt_decode<Jwt>(token).dataExpiracao;

    if (formattedDate) {
        return formatarData(formattedDate);
    } else {
        return '';
    }
}

function formatarData(dataIso: string): string {
    const partes = dataIso.split('/');

    if (partes.length === 3 && partes[0].length === 2 && partes[1].length === 2 && partes[2].length === 4) {
        return `${partes[1]}/${partes[0]}/${partes[2]}`;
    }

    return dataIso;
}


export function obterEmailUsuario(token: string): string {
    if (!token) {
        return '';
    }

    let email = jwt_decode<Jwt>(token).email;
    return email
        ? email
        : '';
}

export function obterLimiteVeiculos(token: string): number {
    if (!token) {
        return 0;
    }

    let limiteVeiculos = jwt_decode<Jwt>(token).limiteDeVeiculos;
    return limiteVeiculos
        ? limiteVeiculos
        : 5;
}

export async function obterTipoPlano(token?: string): Promise<string> {
    if (!token) {
        return 'PRO';
    }

    const idTenant = obterIdTenent(token);

    if (!idTenant) {
        return 'PRO';
    }

    try {
        const response = await api.get<string>(
            `/Empresa/ObterTipoPlano/${idTenant}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        return response.data || 'PRO';
    } catch (error) {
        console.error('Erro ao obter tipo do plano:', error);
        return 'PRO';
    }
}

export function obterEhAdmin(token: string): boolean {
    if (!token) {
        return false;
    }

    let admin = jwt_decode<Jwt>(token).administrador === 'True';
    return admin;
}

export function obterPermissoes(token: string): Set<string> {
    if (!token) {
        return new Set();
    }
    return permissoesEfetivas(jwt_decode<DadosPermissoesDoToken>(token));
}

export function usuarioTemPermissao(token: string, permissao: string): boolean {
    return obterPermissoes(token).has(permissao);
}

export function usuarioTemAlgumaPermissao(token: string, permissoes: string[]): boolean {
    return permissoes.some(p => usuarioTemPermissao(token, p));
}

export function obterDataCadastro(token: string): string {
    if (!token) {
        return '';
    }

    let dataCadastro = jwt_decode<Jwt>(token).dataCadastro;
    return dataCadastro ? dataCadastro : '';
}

export function obterIntegradoLocaSmartCheck(token: string): boolean {
    if (!token) return false;
    const decoded = jwt_decode<Jwt>(token);
    return decoded.integradoLocaSmartCheck === 'True';
}

export function obterEmpresaTemFiliais(token: string): boolean {
    if (!token) return false;
    const decoded = jwt_decode<Jwt>(token);
    return decoded.empresaTemFiliais === 'True';
}

export function obterPossuiSite(token: string): boolean {
    if (!token) return false;
    const decoded = jwt_decode<Jwt>(token);
    return decoded.possuiSite === 'True';
}

export function tokenExpirado(token: string): boolean {
    if (!token) {
        return true;
    }

    let exp = jwt_decode<Jwt>(token).exp;

    if (exp) {
        return ((new Date(exp * 1000)) < (new Date()));
    } else {
        return true;
    }
}

export function onboardingPendente(token: string): boolean {
    if (!token) {
        return false;
    }
    if (localStorage.getItem(chaveOnboardingConcluido(token)) === token) {
        return false;
    }
    return String(jwt_decode<Jwt>(token).onboardingPendente).toLowerCase() === 'true';
}

export function chaveOnboardingConcluido(token: string): string {
    return `onboarding_concluido_${obterIdTenent(token)}`;
}
